import { useCallback } from "react"
import { toast } from "react-hot-toast"
import useAuth from "../../hooks/useAuth"
import { verifySchemePayment } from "../../service/scheme"
import STRING_CONSTANTS from "../../shared/constants/StringConstants"
import useSchemeBusiness from "./useSchemeBusiness"
import { PAYMENT_NOTICE_DURATION_MS, PAYMENT_TOAST_ID } from "./constants"
import {
  buildCheckoutOptions,
  getPaymentFailureMessage,
  getVerifyPayload,
  isNoResponseError,
} from "./payment"

const CHECKOUT_SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js"

let checkoutScript = null

const loadCheckoutScript = () => {
  if (window.Razorpay) return Promise.resolve()
  if (!checkoutScript) {
    checkoutScript = new Promise((resolve, reject) => {
      const script = document.createElement("script")
      script.src = CHECKOUT_SCRIPT_URL
      script.async = true
      script.onload = resolve
      script.onerror = () => {
        checkoutScript = null
        script.remove()
        reject(new Error("Razorpay Checkout could not be loaded"))
      }
      document.body.appendChild(script)
    })
  }
  return checkoutScript
}

export const openRazorpayCheckout = (
  token,
  business,
  checkout,
  { onVerified, onError, onClose, onPending }
) =>
  loadCheckoutScript().then(() => {
    let failure = null
    let verifying = false
    const razorpay = new window.Razorpay(
      buildCheckoutOptions(checkout, {
        onSuccess: (response) => {
          verifying = true
          toast.loading(STRING_CONSTANTS.PAYMENT_CONFIRMING, {
            id: PAYMENT_TOAST_ID,
          })
          return verifySchemePayment({
            token,
            business,
            ...getVerifyPayload(response),
          })
            .then(onVerified)
            .catch((error) => {
              if (!isNoResponseError(error)) return onError(error)
              toast(STRING_CONSTANTS.PAYMENT_UNCONFIRMED, {
                id: PAYMENT_TOAST_ID,
                duration: PAYMENT_NOTICE_DURATION_MS,
              })
              onClose()
              onPending()
            })
        },
        onDismiss: () => {
          if (verifying) return
          if (failure) {
            toast.error(getPaymentFailureMessage(failure), {
              id: PAYMENT_TOAST_ID,
            })
            onClose()
            return
          }
          toast(STRING_CONSTANTS.PAYMENT_NOT_COMPLETED, {
            id: PAYMENT_TOAST_ID,
            duration: PAYMENT_NOTICE_DURATION_MS,
          })
          onClose()
          onPending()
        },
      })
    )
    razorpay.on("payment.failed", (response) => {
      failure = response?.error || {}
    })
    razorpay.open()
  })

const useRazorpayCheckout = () => {
  const { token } = useAuth()
  const business = useSchemeBusiness()

  return useCallback(
    (checkout, handlers) =>
      openRazorpayCheckout(token, business, checkout, handlers),
    [token, business]
  )
}

export default useRazorpayCheckout
